/**
 * Контекст авторизации
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import authService from '@/services/authService';
import type { User, AuthContextType } from '@/types/auth';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const isAuthenticated = !!user;

  /**
   * Проверить авторизацию при загрузке приложения
   */
  const checkAuth = async () => {
    setIsLoading(true);
    try {
      if (authService.isAuthenticated()) {
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
      }
    } catch (error) {
      console.error('Ошибка проверки авторизации:', error);
      authService.removeToken();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Вход в систему
   */
  const login = async (username: string, password: string) => {
    try {
      const response = await authService.login(username, password);

      // Получаем полную информацию о пользователе
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);

      toast.success(`Добро пожаловать, ${currentUser.full_name || currentUser.username}!`, {
        duration: 3000, // Автоматически исчезнет через 3 секунды
      });

      // Перенаправляем на главную страницу
      navigate('/');
    } catch (error: any) {
      console.error('Ошибка входа:', error);

      const errorMessage = error.response?.data?.detail || 'Ошибка входа в систему';
      toast.error(errorMessage);

      throw error;
    }
  };

  /**
   * Выход из системы
   */
  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      toast.success('Вы вышли из системы');
      navigate('/login');
    } catch (error) {
      console.error('Ошибка выхода:', error);
      // Даже если произошла ошибка на сервере, удаляем локальные данные
      authService.removeToken();
      setUser(null);
      navigate('/login');
    }
  };

  // Проверяем авторизацию при монтировании
  useEffect(() => {
    checkAuth();
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Хук для использования AuthContext
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }

  return context;
};
